<template>
  <div class="business-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">
          <span v-for="(part, index) in pageTitleParts" :key="index" class="title-part">{{ part }}</span>
        </h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 富文本内容 -->
        <div
          v-else-if="contentHtml"
          class="richtext-content"
          v-html="contentHtml"
        ></div>

        <!-- 空状态 -->
        <div
          v-else
          class="empty-state"
        >
          <div class="empty-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 70C56.5685 70 70 56.5685 70 40C70 23.4315 56.5685 10 40 10C23.4315 10 10 23.4315 10 40C10 56.5685 23.4315 70 40 70Z"
                stroke="var(--el-color-primary-light-8)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M40 30V40"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M40 50H40.025"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">{{ t('common.no_data') }}</h3>
          <p class="empty-description">{{ t('common.no_content') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.explosives')
})

const pageTitleParts = computed(() => {
  const title = pageTitle.value
  return title.split(/(?<=[：:])\s*|\s+/).filter(part => part.trim())
})

const contentHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/overseas-business' // 使用当前路由路径
    })

    if (response) {
      pageData.value = response
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})


// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})
</script>

<style scoped lang="scss">

// 导入全局样式变量
@import '@/assets/css/business.scss';
</style>